// The app's one document viewer: showDocument fills it and <DocumentViewer /> draws it as a modal,
// so documents open over the page instead of in another tab

const TYPES = { pdf: "application/pdf", png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", gif: "image/gif", webp: "image/webp" };

let current = null;
const listeners = new Set();

const set = (next) => {
  current = next;
  listeners.forEach((listener) => listener());
};

export const subscribeViewer = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getViewer = () => current;

export const closeViewer = () => {
  if (current?.url) URL.revokeObjectURL(current.url);
  set(null);
};

// The first bytes of a PDF, PNG and JPEG, for files that come without a name
const SIGNATURES = [
  ["application/pdf", [0x25, 0x50, 0x44, 0x46]],
  ["image/png", [0x89, 0x50, 0x4e, 0x47]],
  ["image/jpeg", [0xff, 0xd8, 0xff]],
];

const sniff = async (blob) => {
  const head = new Uint8Array(await blob.slice(0, 4).arrayBuffer());
  return SIGNATURES.find(([, bytes]) => bytes.every((b, i) => head[i] === b))?.[0];
};

// Files come as attachments, often typed application/octet-stream: the name's extension, or else the content, tells the viewer what they are
const typed = async (blob, fileName) => {
  if (blob.type && blob.type !== "application/octet-stream") return blob;
  const type = TYPES[fileName.split(".").pop()?.toLowerCase()] ?? (await sniff(blob));
  return type ? new Blob([blob], { type }) : blob;
};

/**
 * Opens the viewer at once with a spinner, then shows the Blob `load` resolves to. A viewer closed
 * or replaced while loading drops the result; a failed load closes it and rethrows for the caller to report.
 */
export const showDocument = async (load, fileName = "") => {
  closeViewer();
  const id = Symbol(fileName);
  set({ id, fileName, status: "loading" });
  try {
    const blob = await typed(await load(), fileName);
    if (current?.id !== id) return;
    set({ id, fileName, status: "ready", type: blob.type, url: URL.createObjectURL(blob) });
  } catch (error) {
    if (current?.id === id) set(null);
    throw error;
  }
};
